import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { GenericRechartsTooltip } from "../../components/CustomMUI";
import PersonIcon from "@mui/icons-material/Person";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";

export default function Directors({ data, isOwnProfile, subject }) {
  const theme = useTheme();
  const isBelowSm = useMediaQuery(theme.breakpoints.down("sm"));
  const featuredDirector = data.directors.highest_rated[0]
    ? { type: "highest-rated", ...data.directors.highest_rated[0] }
    : data.directors.most_watched[0]
      ? { type: "most-watched", ...data.directors.most_watched[0] }
      : null;

  const featuredDirectorCaption = (
    <>
      <Typography
        variant="caption"
        component="div"
        sx={{
          lineHeight: 1.2,
          mt: 2,
          ml: { xs: 0, sm: 2 },
        }}
      >
        {isOwnProfile ? (
          "Your"
        ) : (
          <>
            {subject}
            <em>’s</em>
          </>
        )}{" "}
        {featuredDirector?.type ?? "highest-rated"} director is{" "}
        <b>{featuredDirector?.name ?? "currently unknown"}</b>
      </Typography>

      {featuredDirector && featuredDirector.type === "highest-rated" && (
        <Typography
          variant="caption"
          component="div"
          sx={{
            lineHeight: 1.2,
            mt: 1,
            ml: { xs: 0, sm: 2 },
          }}
        >
          {isOwnProfile ? "You" : "They"} rated {featuredDirector.rating_count}{" "}
          of their films, with an average rating of{" "}
          {featuredDirector.average_rating.toFixed(1)}/5
        </Typography>
      )}
    </>
  );

  const highestRatedDirectorsChart = (
    <Stack spacing={1} alignItems={"center"}>
      <Typography variant="subtitle2">Highest-rated</Typography>
      {data.directors.highest_rated.length === 0 ? (
        <Box maxWidth={200}>
          <Typography
            variant="body2"
            sx={{
              fontStyle: "italic",
              textAlign: "center",
              fontSize: 12,
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            Not enough data yet{" "}
            {isOwnProfile
              ? "- rate a few more films from the same director to see results here"
              : ""}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: data.directors.highest_rated.length * 28,
          }}
        >
          <ResponsiveContainer>
            <BarChart
              data={data.directors.highest_rated}
              layout="vertical"
              margin={{ top: 0, right: 25, bottom: 0, left: 10 }}
            >
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 12 }}
                width={120}
              />

              <XAxis type="number" hide domain={[0, 5]} />

              <Tooltip
                cursor={{ fill: theme.palette.action.hover }}
                content={
                  <GenericRechartsTooltip
                    renderContent={({ average_rating, rating_count }) => (
                      <>
                        Average of {average_rating.toFixed(1)} based on{" "}
                        {rating_count} ratings
                      </>
                    )}
                  />
                }
              />

              <Bar
                dataKey="average_rating"
                fill={theme.palette.primary.main}
                radius={[
                  0,
                  theme.shape.borderRadius,
                  theme.shape.borderRadius,
                  0,
                ]}
              >
                <LabelList
                  dataKey="average_rating"
                  position="right"
                  formatter={(value) => value.toFixed(1)}
                  style={{
                    fill: theme.palette.text.primary,
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Stack>
  );

  const mostWatchedDirectorsChart = (
    <Stack spacing={1} alignItems="center">
      <Typography variant="subtitle2">Most-watched</Typography>
      {data.directors.most_watched.length === 0 ? (
        <Box maxWidth={200}>
          <Typography
            variant="body2"
            sx={{
              fontStyle: "italic",
              textAlign: "center",
              fontSize: 12,
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            Not enough data yet{" "}
            {isOwnProfile
              ? "- log a few more films from the same director to see results here"
              : ""}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: data.directors.most_watched.length * 28,
          }}
        >
          <ResponsiveContainer>
            <BarChart
              data={data.directors.most_watched}
              layout="vertical"
              margin={{ top: 0, right: 20, bottom: 0, left: 10 }}
            >
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 12 }}
                width={120}
              />

              <XAxis type="number" hide domain={[0, "dataMax"]} />

              <Tooltip
                cursor={{ fill: theme.palette.action.hover }}
                content={
                  <GenericRechartsTooltip
                    renderContent={({ watch_count }) => {
                      const total = data.counts.finished_count;
                      const percentage =
                        total > 0 ? Math.round((watch_count / total) * 100) : 0;

                      return (
                        <>
                          {watch_count} films ({percentage}% of total)
                        </>
                      );
                    }}
                  />
                }
              />

              <Bar
                dataKey="watch_count"
                fill={theme.palette.primary.main}
                radius={[
                  0,
                  theme.shape.borderRadius,
                  theme.shape.borderRadius,
                  0,
                ]}
              >
                <LabelList
                  dataKey="watch_count"
                  position="right"
                  style={{
                    fill: theme.palette.text.primary,
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Stack>
  );

  return (
    <>
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}
      >
        <Box
          sx={{
            flex: "0 0 auto",
            minWidth: 0,
            height: "100%",
            width: { xs: 80, sm: 150 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: { xs: 80, sm: 150 },
              borderRadius: "50%",
              overflow: "hidden",
              backgroundColor: "action.hover",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {data.directors.featured_image_url ? (
              <Box
                component="img"
                src={data.directors.featured_image_url}
                alt="Highest-rated director image"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : (
              <QuestionMarkIcon color="primary" fontSize="large" />
            )}
          </Box>
          {!isBelowSm && featuredDirectorCaption}
        </Box>
        <Box
          sx={{
            flex: "1 1 auto",
            minWidth: 0, // 🔑 prevents overflow issues later
            height: "100%",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems={isBelowSm ? "flex-start" : "center"}
              >
                <PersonIcon color="primary" />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", lineHeight: 1 }}
                >
                  {isOwnProfile ? "Your favourite" : "Favourite"} directors...
                </Typography>
              </Stack>
              {isBelowSm && featuredDirectorCaption}
            </Grid>
            {!isBelowSm && (
              <>
                <Grid item xs={12} sm={6}>
                  {highestRatedDirectorsChart}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {mostWatchedDirectorsChart}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Grid>
      {isBelowSm && (
        <>
          <Grid item xs={12} sm={6}>
            {highestRatedDirectorsChart}
          </Grid>
          <Grid item xs={12} sm={6}>
            {mostWatchedDirectorsChart}
          </Grid>
        </>
      )}
    </>
  );
}
